<script lang="ts" setup>
import { defineEmits, provide, ref } from "vue";

import MInput from "../MInput";
import { ItemOption } from "./_types";
import SimpleMenu from "../SearchRenderers/SimpleMenu.vue";
import SelectRenderDrugList from "../SearchRenderers/SelectRenderDrugList.vue";
import useMSearchSelect from "./useMSearchSelect";

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    clearable?: boolean;
    debounce?: string | number;
    for?: string;
    instructions?: string;
    itemMap?: (v: any) => any;
    label?: string;
    minSearchValueLength?: number;
    modelValue?: any;
    name?: string;
    placeholder?: string;
    requiredText?: string;
    closeOnClickOutside?: boolean;
    closeOnScroll?: boolean | null;
    searchFilter?: (item: string) => boolean;
    searchUrl: string;
    useInput?: boolean;
  }>(),
  {
    useInput: undefined,
    debounce: 10,
    closeOnScroll: null,
  },
);

const inputEl = ref<InstanceType<typeof MInput> | null>(null);

const emit = defineEmits({
  "update:modelValue": (_value: any) => true,
  "input-value": (_option: ItemOption) => true,
  "search-results": (_results: Array<any>) => true,
  blur: () => true,
});

const {
  displayErrors,
  focus,
  focusedItem,
  filteredItems,
  focusedItemId,
  handleInput,
  id,
  isFetching,
  isInput,
  isMousedown,
  isOpen,
  menuId,
  onClear,
  onClick,
  onKeydown,
  onSelect,
  open,
  selectText,
  text,
  value,
} = useMSearchSelect(props, { emit, inputEl });

provide("formData", undefined);
provide("validationController", undefined);

defineExpose({
  open,
  focus,
  clear: () => onClear,
  isOpen: isOpen,
  isInput,
  text,
});
//
// console.log(inputEl.value);
// watch(inputEl, () => console.log("watch", inputEl.value));
</script>
<template>
  <MInput
    ref="inputEl"
    v-bind="$props"
    v-model="text"
    :class="['m-search-select', isInput && 'use-input']"
    :for="id"
    :instructions="instructions"
    :autofocus="autofocus"
    :placeholder="placeholder"
    :clearable="clearable || !!searchUrl"
    :loading="isFetching"
    :aria-controls="menuId"
    :aria-owns="menuId"
    :aria-expanded="isOpen"
    :aria-activedescendant="focusedItemId"
    sr-instructions-id="form-required-text-description"
    debounce="10"
    input-role="combobox"
    data-form-type="other"
    no-validation
    :readonly="$attrs.readonly || !isInput"
    :show-clear="clearable && value !== null"
    :display-errors="displayErrors"
    @keydown="onKeydown"
    @click="onClick"
    @clear="onClear"
    @mousedown="isMousedown = true"
    @input="handleInput"
    @focus="
      () => {
        if (isMousedown === false) {
          selectText();
        }
      }
    "
  >
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>

    <template #default="{ el }">
      <SimpleMenu
        v-if="filteredItems.length > 0 && $attrs.readonly !== true"
        :id="menuId"
        v-model="isOpen"
        no-auto-open
        no-mouseleave-close
        :limit-width-to-target="true"
        role="listbox"
        :target="el"
        @keydown="onKeydown"
      >
        <SelectRenderDrugList
          :focused-item="focusedItem"
          :items="filteredItems"
          :value="value"
          :disabled="isFetching"
          :data-testid="$attrs['data-testid']"
          @select="onSelect"
        >
        </SelectRenderDrugList>
      </SimpleMenu>
    </template>
  </MInput>
</template>
<style lang="scss">
@import "MSearchSelect";
</style>
